import is from '@sindresorhus/is';
import { type FunctionComponent, type KeyboardEvent, useState } from 'react';

import { Button } from '~/ui/core/button/Button';
import { Input } from '~/ui/core/input/Input';
import { ChangeProposalCard } from '~/ui/domain/chat/ChangeProposalCard';

import {
    HELLO_HITL_TOOL_ID,
    type HelloHitlResponseEvent,
    type QuestionnaireBlock,
    type QuestionnaireQuestion,
} from '../../data-contract/chat';
import { useSubmitChatInteractionCallback } from '../api';
import type { StructuredRenderer } from './types';

type QuestionCardProps = {
    readonly block: QuestionnaireBlock;
    readonly eventId: string;
    readonly interactive: boolean;
    readonly question: QuestionnaireQuestion;
};

const submitCustomFromKey = (
    event: KeyboardEvent<HTMLInputElement>,
    draft: string,
    onSubmit: (value: string) => void,
): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const value = draft.trim();
    if (value.length > 0) onSubmit(value);
};

const QuestionCard: FunctionComponent<QuestionCardProps> = ({ block, eventId, interactive, question }) => {
    const callback = useSubmitChatInteractionCallback();
    const [draft, setDraft] = useState('');
    const [localAnswer, setLocalAnswer] = useState<string | null>(null);

    const answer = question.answer ?? localAnswer;

    const submit = (value: string): void => {
        setLocalAnswer(value);
        callback.mutate({
            blockId: block.blockId,
            eventId,
            payload: {
                actions: [{ actionId: question.actionId, input: value, type: 'input' }],
            },
        });
    };

    if (!is.null(answer)) {
        return (
            <ChangeProposalCard className="ml-1">
                <ChangeProposalCard.Header label={block.title} />
                <ChangeProposalCard.Body>
                    <ChangeProposalCard.Text>{question.label}</ChangeProposalCard.Text>
                </ChangeProposalCard.Body>
                <ChangeProposalCard.Actions>
                    <Button className="w-full bg-green-700 text-white-200" disabled={true} size="sm">
                        {answer}
                    </Button>
                </ChangeProposalCard.Actions>
            </ChangeProposalCard>
        );
    }

    const canInteract = interactive && !callback.isPending;
    const allowCustomInput = question.allowCustomInput === true;

    return (
        <ChangeProposalCard className="ml-1">
            <ChangeProposalCard.Header label={block.title} />
            <ChangeProposalCard.Body>
                <ChangeProposalCard.Text>{question.label}</ChangeProposalCard.Text>
            </ChangeProposalCard.Body>
            <ChangeProposalCard.Actions>
                <div className="flex flex-wrap gap-s">
                    {question.options.map((option) => (
                        <Button
                            key={option.label}
                            disabled={!canInteract}
                            className="w-full"
                            size="sm"
                            onClick={() => submit(option.label)}
                        >
                            {option.recommended === true ? `★ ${option.label}` : option.label}
                        </Button>
                    ))}
                </div>
                {allowCustomInput ? (
                    <Input
                        className="min-w-0 flex-1"
                        disabled={!canInteract}
                        placeholder="Type your own answer…"
                        type="text"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={(event) => submitCustomFromKey(event, draft, submit)}
                    />
                ) : null}
            </ChangeProposalCard.Actions>
        </ChangeProposalCard>
    );
};

const HelloHitlInline: FunctionComponent<{
    readonly event: HelloHitlResponseEvent;
    readonly interactive: boolean;
}> = ({ event, interactive }) => (
    <div className="flex flex-col gap-s">
        {event.payload.blocks.map((block) =>
            block.questions.map((question) => (
                <QuestionCard
                    key={`${block.blockId}-${question.actionId}`}
                    block={block}
                    eventId={event.eventId}
                    interactive={interactive}
                    question={question}
                />
            )),
        )}
    </div>
);

export const helloHitlRenderer: StructuredRenderer<HelloHitlResponseEvent> = {
    renderInline: (event, context) => <HelloHitlInline event={event} interactive={context.interactive} />,
    toolId: HELLO_HITL_TOOL_ID,
};
